import React, { FC, Suspense } from 'react';
import { BrowserRouter, Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import PrivateRoutes from './routes/private.routes';
import BidderRoutes from './routes/bidder.routes';
import PublicRoutes from './routes/public.routes';
import AppLayout from './components/AppLayout';
import BidderAccountLayout from './pages/BidderPortal/BidderAccountLayout';
import ActivityNotificationHost from './features/activityNotifications/ActivityNotificationHost';
import ProjectTitleManager from './components/ProjectTitleManager';
import { getStoredAccessToken, shouldMountAuthenticatedGlobalHosts } from './helpers/authSession';
import { USER_ROLE } from './helpers/Constants';
import { Button, Loader } from './style';
import {
	MultiViewHost,
	MultiViewProvider,
	useMultiView,
} from './features/multiView/MultiViewContext';
import { AutopilotProvider, AutopilotShell, isAutopilotUiEnabled } from './features/autopilot';
import { UndoActionProvider } from './components/UndoAction';

const AUTOPILOT_UI_ENABLED = isAutopilotUiEnabled();
const isBidderAccount = () => localStorage.getItem(USER_ROLE) === 'BIDDER';
const BIDDER_SAFE_PUBLIC_ROUTE_NAMES = new Set([
	'BidderPortal',
	'BidderAccountSetup',
	'ForgotPassword',
	'ForgotPasswordSuccess',
	'ResetPassword',
	'BidderResetPassword',
	'TermsConditions',
	'Privacypolicy',
]);
const BidderPublicRoutes = PublicRoutes.filter((route) =>
	BIDDER_SAFE_PUBLIC_ROUTE_NAMES.has(route.name),
);

const AuthenticatedGlobalHosts: FC = () => {
	const { pathname } = useLocation();
	const { isOpen } = useMultiView();
	return !isOpen && shouldMountAuthenticatedGlobalHosts(pathname) ? (
		<ActivityNotificationHost />
	) : null;
};

const NotFound: FC = () => (
	<div
		style={{
			position: 'absolute',
			top: '50%',
			left: '50%',
			marginRight: '-50%',
			transform: 'translate(-50%, -50%)',
		}}
	>
		<h1 style={{ fontSize: '50px' }}>404</h1>
		<h1 style={{ fontSize: '50px' }}>Page not found!</h1>
		{(localStorage.getItem(USER_ROLE) === 'U' ||
			localStorage.getItem(USER_ROLE) === 'OA' ||
			localStorage.getItem(USER_ROLE) === 'A') && (
			<div style={{ padding: '15%' }}>
				<Link to="/home">
					<Button btnPrimary>Home</Button>
				</Link>
			</div>
		)}
		{localStorage.getItem(USER_ROLE) === 'PO' && (
			<div style={{ padding: '15%' }}>
				<Link to="/dashboard">
					<Button btnPrimary>Dashboard</Button>
				</Link>
			</div>
		)}
		{isBidderAccount() && (
			<div style={{ padding: '15%' }}>
				<Link to="/bidder/profile">
					<Button btnPrimary>Bidder profile</Button>
				</Link>
			</div>
		)}
		{!localStorage.getItem(USER_ROLE) && (
			<div style={{ padding: '15%' }}>
				<Link to="/">
					<Button btnPrimary>Login</Button>
				</Link>
			</div>
		)}
	</div>
);

const BidderRouteSwitch: FC = () => (
	<Switch>
		{BidderPublicRoutes.map((route) => {
			const { Component } = route;
			return (
				<Route key={route.path} {...route}>
					<Suspense fallback={<Loader />}>
						<Component />
					</Suspense>
				</Route>
			);
		})}
		{getStoredAccessToken() &&
			BidderRoutes.map((route) => {
				const { Component } = route;
				return (
					<Route key={route.path} {...route}>
						<BidderAccountLayout>
							<Suspense fallback={<Loader />}>
								<Component />
							</Suspense>
						</BidderAccountLayout>
					</Route>
				);
			})}
		<Route exact path="/">
			<Redirect to="/bidder/profile" />
		</Route>
		<Route>
			<NotFound />
		</Route>
	</Switch>
);

const BidderApplication: FC = () => (
	<MultiViewProvider>
		<MultiViewHost>
			<BidderRouteSwitch />
		</MultiViewHost>
	</MultiViewProvider>
);

const StaffRoutes: FC = () => (
	<Switch>
		{PublicRoutes.map((route) => {
			const { Component } = route;
			return (
				<Route key={route.path} {...route}>
					<Suspense fallback={<Loader />}>
						<Component />
					</Suspense>
				</Route>
			);
		})}
		{getStoredAccessToken() &&
			PrivateRoutes.map((route) => {
				const { Component } = route;
				return (
					<Route key={route.path} {...route}>
						<AppLayout {...route}>
							<Suspense fallback={<Loader />}>
								<Component />
							</Suspense>
						</AppLayout>
					</Route>
				);
			})}
		<Route>
			<NotFound />
		</Route>
	</Switch>
);

const StaffApplication: FC = () => (
	<UndoActionProvider>
		<MultiViewProvider>
			{AUTOPILOT_UI_ENABLED ? (
				<AutopilotProvider enabled>
					<AutopilotShell>
						<ProjectTitleManager />
						<MultiViewHost>
							<StaffRoutes />
						</MultiViewHost>
					</AutopilotShell>
				</AutopilotProvider>
			) : (
				<>
					<ProjectTitleManager />
					<AuthenticatedGlobalHosts />
					<MultiViewHost>
						<StaffRoutes />
					</MultiViewHost>
				</>
			)}
		</MultiViewProvider>
	</UndoActionProvider>
);

const ApplicationRouter: FC = () => {
	const { pathname } = useLocation();
	const isPublicBidInvitation = pathname.indexOf('/bid/') === 0;
	return isPublicBidInvitation || isBidderAccount() ? <BidderApplication /> : <StaffApplication />;
};

const AppRoute: FC = () => (
	<BrowserRouter>
		<ApplicationRouter />
	</BrowserRouter>
);

export default AppRoute;
